import { lazy, type ComponentType } from 'react';

/**
 * Wrapper de React.lazy com retry automático quando a chunk falha por
 * flutuação de rede (timeout, conexão instável).
 *
 * Sem isso: 1 timeout = lazy() rejeita, ErrorBoundary captura e a aba quebra
 * até o consultor dar refresh manualmente.
 *
 * Com isso: tenta 3x com backoff exponencial (500ms, 1500ms). Se todas
 * falharem, propaga o erro e o handler global de chunk error do main.tsx
 * oferece reload (ou o ErrorBoundary local mostra o botão).
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  retries = 3,
  baseDelay = 500,
): ReturnType<typeof lazy<T>> {
  return lazy(() => retryImport(factory, retries, baseDelay));
}

async function retryImport<M>(
  factory: () => Promise<M>,
  retries: number,
  baseDelay: number,
): Promise<M> {
  let ultimoErro: unknown;
  for (let tentativa = 0; tentativa < retries; tentativa++) {
    try {
      return await factory();
    } catch (err) {
      ultimoErro = err;
      const msg = (err as Error)?.message || String(err);
      // Só faz retry se for erro de chunk (rede); outros erros (TS, runtime)
      // propaga imediatamente — retry não vai resolver.
      const isChunkErr = /Failed to fetch dynamically imported module|Loading chunk \d+ failed|ChunkLoadError|importing the module/i.test(msg);
      if (!isChunkErr) throw err;
      if (tentativa < retries - 1) {
        // Backoff exponencial: 500ms, 1500ms
        await new Promise((r) => setTimeout(r, baseDelay * Math.pow(3, tentativa)));
      }
    }
  }
  throw ultimoErro;
}
